import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStructuredData } from '@/hooks/useStructuredData';

const ABOUT_SCHEMAS = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Onetvservices — Premium IPTV Provider Since 2019',
    url: 'https://onetvservices.com/about',
    description: 'Learn about Onetvservices, a trusted IPTV streaming provider since 2019 serving 10,000+ subscribers in 150+ countries with +25,000 live channels and +70,000 VOD titles.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://onetvservices.com' },
        { '@type': 'ListItem', position: 2, name: 'About Us', item: 'https://onetvservices.com/about' },
      ],
    },
    mainEntity: {
      '@type': 'Organization',
      name: 'Onetvservices',
      url: 'https://onetvservices.com',
      foundingDate: '2019',
      email: 'onetvservices@gmail.com',
      telephone: '+1-208-974-6525',
      numberOfEmployees: { '@type': 'QuantitativeValue', value: 10 },
      areaServed: 'Worldwide',
    },
  },
];

const STATS = [
  { icon: 'ri-calendar-check-line', value: '7+', label: 'Years in Business', color: 'from-sky-500/20 to-cyan-500/10', text: 'text-sky-400' },
  { icon: 'ri-group-line', value: '10,000+', label: 'Active Subscribers', color: 'from-emerald-500/20 to-green-500/10', text: 'text-emerald-400' },
  { icon: 'ri-tv-2-line', value: '+25,000', label: 'Live Channels', color: 'from-amber-500/20 to-orange-500/10', text: 'text-amber-400' },
  { icon: 'ri-global-line', value: '150+', label: 'Countries Covered', color: 'from-rose-500/20 to-pink-500/10', text: 'text-rose-400' },
];

const VALUES = [
  {
    icon: 'ri-shield-check-line',
    title: 'Transparency',
    desc: 'We are upfront about what our service includes and what it does not. No hidden fees, no misleading claims — just honest streaming.',
    color: 'text-sky-400',
    bg: 'bg-sky-500/10 border-sky-500/20',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Customer First',
    desc: 'Our support team is available 7 days a week from 10am to midnight. We respond in under 5 minutes on WhatsApp.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10 border-emerald-500/20',
  },
  {
    icon: 'ri-award-line',
    title: 'Quality',
    desc: 'We invest in premium server infrastructure and Anti-Freeze technology to deliver a stable, buffer-free experience in HD and 4K.',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/20',
  },
  {
    icon: 'ri-lock-line',
    title: 'Privacy & Security',
    desc: 'Your personal data is never sold or shared with third parties. All transactions are encrypted and handled securely.',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10 border-rose-500/20',
  },
];

const TEAM = [
  {
    name: 'Operations Team',
    role: 'Service Management & Activation',
    desc: 'Handles all subscription activations, account management, and service delivery — ensuring you are up and running in under 5 minutes.',
    icon: 'ri-settings-3-line',
    color: 'from-sky-500/20 to-cyan-500/10',
    text: 'text-sky-400',
  },
  {
    name: 'Technical Support',
    role: 'Installation & Troubleshooting',
    desc: 'Expert technicians available 7 days a week to help you install the service on any device and resolve any technical issues.',
    icon: 'ri-tools-line',
    color: 'from-emerald-500/20 to-green-500/10',
    text: 'text-emerald-400',
  },
  {
    name: 'Customer Care',
    role: 'Billing, Refunds & Satisfaction',
    desc: 'Dedicated to ensuring your complete satisfaction — from handling refund requests to answering billing questions promptly.',
    icon: 'ri-heart-line',
    color: 'from-amber-500/20 to-orange-500/10',
    text: 'text-amber-400',
  },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useStructuredData(ABOUT_SCHEMAS);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header nav */}
      <div className="bg-gray-900 border-b border-white/10 py-6 px-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-lg" />
            <span className="text-sm">Back to Home</span>
          </Link>
          <div className="w-px h-5 bg-white/20" />
          <Link to="/">
            <img
              src="https://storage.readdy-site.link/project_files/ea087323-c332-4d1a-a768-642201e38806/8e03886a-a466-4209-8fc0-30625673693c_ONETV.png?v=410354e2b00bf17cf48639a5651635ec"
              alt="Onetvservices Logo"
              className="h-12 w-auto object-contain"
            />
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-sky-500/6 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-amber-500/5 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-5xl mx-auto px-4 py-16 sm:py-24 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-sky-500/10 border border-sky-500/20 rounded-full px-4 py-1.5 text-sky-400 text-sm font-medium mb-6">
              <i className="ri-information-line" />
              About Us
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 leading-tight">
              Who is{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-cyan-400">
                Onetvservices?
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
              A trusted IPTV streaming provider since 2019, serving over 10,000 customers worldwide with premium content, reliable infrastructure, and dedicated support.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {STATS.map(({ icon, value, label, color, text }) => (
              <div key={label} className={`bg-gradient-to-br ${color} border border-white/8 rounded-2xl p-4 sm:p-5 text-center`}>
                <div className={`w-10 h-10 mx-auto rounded-xl bg-white/5 flex items-center justify-center mb-3`}>
                  <i className={`${icon} ${text} text-xl`} />
                </div>
                <p className={`text-2xl sm:text-3xl font-black ${text} mb-1`}>{value}</p>
                <p className="text-gray-400 text-xs sm:text-sm">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pb-20 space-y-16">

        {/* Our Story */}
        <section>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-gray-400 text-xs font-medium mb-4">
                <i className="ri-history-line" /> Our Story
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-5 leading-tight">
                7 Years of Premium<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-cyan-400">Streaming Excellence</span>
              </h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Onetvservices was founded in 2019 with a simple mission: to make premium international TV content accessible and affordable for everyone, regardless of where they live.
                </p>
                <p>
                  What started as a small operation serving a handful of customers has grown into a trusted platform with over <strong className="text-white">10,000 active subscribers</strong> across Europe, Africa, North America, and the Middle East.
                </p>
                <p>
                  We specialise in providing access to <strong className="text-white">+25,000 live TV channels</strong> and <strong className="text-white">+70,000 movies &amp; series</strong> in HD and 4K quality, with a focus on stability, speed, and customer satisfaction.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20professional%20streaming%20media%20company%20office%20team%20working%20on%20screens%20with%20television%20content%2C%20warm%20lighting%2C%20clean%20minimalist%20workspace%2C%20technology%20company%20atmosphere%2C%20dark%20theme&width=600&height=420&seq=about-story-01&orientation=landscape"
                alt="Onetvservices team"
                className="w-full h-[280px] sm:h-[340px] object-cover object-top rounded-2xl"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-gray-950/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center gap-3 bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-500/20 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-emerald-400 text-sm" />
                  </div>
                  <div>
                    <p className="text-white text-xs font-semibold">Serving customers worldwide</p>
                    <p className="text-gray-400 text-[11px]">Europe · Africa · Americas · Middle East</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Business Identity */}
        <section>
          <div className="bg-gray-900/60 border border-white/8 rounded-3xl p-6 sm:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-sky-500/15 rounded-2xl flex-shrink-0">
                <i className="ri-building-line text-sky-400 text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-white mb-1">Business Information</h2>
                <p className="text-gray-500 text-sm">Official identity &amp; contact details</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-store-2-line', label: 'Business Name', value: 'Onetvservices', color: 'text-sky-400' },
                { icon: 'ri-calendar-line', label: 'Founded', value: '2019', color: 'text-amber-400' },
                { icon: 'ri-global-line', label: 'Website', value: 'onetvservices.com', color: 'text-emerald-400' },
                { icon: 'ri-mail-line', label: 'Email', value: 'onetvservices@gmail.com', color: 'text-rose-400' },
                { icon: 'ri-phone-line', label: 'Phone / WhatsApp', value: '+1 (208) 974-6525', color: 'text-sky-400' },
                { icon: 'ri-time-line', label: 'Support Hours', value: '7 days/week · 10am – 12am', color: 'text-amber-400' },
                { icon: 'ri-tv-line', label: 'Service Type', value: 'IPTV Streaming — Fixed-term subscriptions', color: 'text-emerald-400' },
                { icon: 'ri-map-2-line', label: 'Service Area', value: 'Worldwide (150+ countries)', color: 'text-rose-400' },
              ].map(({ icon, label, value, color }) => (
                <div key={label} className="flex items-start gap-3 bg-white/4 border border-white/6 rounded-xl p-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/5 rounded-lg flex-shrink-0 mt-0.5">
                    <i className={`${icon} ${color} text-sm`} />
                  </div>
                  <div>
                    <p className="text-gray-500 text-[11px] uppercase tracking-wide font-medium mb-0.5">{label}</p>
                    <p className="text-white text-sm font-semibold">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-gray-400 text-xs font-medium mb-4">
              <i className="ri-heart-line" /> Our Values
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white">What We Stand For</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {VALUES.map(({ icon, title, desc, color, bg }) => (
              <div key={title} className={`border ${bg} rounded-2xl p-5 sm:p-6`}>
                <div className={`w-10 h-10 flex items-center justify-center ${bg} rounded-xl mb-4`}>
                  <i className={`${icon} ${color} text-xl`} />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Our Team */}
        <section>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-gray-400 text-xs font-medium mb-4">
              <i className="ri-team-line" /> Our Team
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white">The People Behind the Service</h2>
            <p className="text-gray-500 text-sm mt-2 max-w-xl mx-auto">A dedicated team working around the clock to deliver the best streaming experience</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {TEAM.map(({ name, role, desc, icon, color, text }) => (
              <div key={name} className={`bg-gradient-to-br ${color} border border-white/8 rounded-2xl p-5`}>
                <div className={`w-12 h-12 flex items-center justify-center bg-white/8 rounded-2xl mb-4`}>
                  <i className={`${icon} ${text} text-2xl`} />
                </div>
                <h3 className="text-white font-bold text-base mb-0.5">{name}</h3>
                <p className={`${text} text-xs font-medium mb-3`}>{role}</p>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Legal Compliance */}
        <section>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-3xl p-6 sm:p-8">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-500/15 rounded-xl flex-shrink-0">
                <i className="ri-scales-line text-amber-400 text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Legal &amp; Compliance Notice</h2>
                <p className="text-gray-500 text-sm">Important information about our service</p>
              </div>
            </div>
            <div className="space-y-3 text-sm text-gray-400 leading-relaxed">
              <p>
                Onetvservices is an <strong className="text-white">independent IPTV reseller</strong>. We are not affiliated with, endorsed by, or partnered with any television broadcaster, streaming platform, sports league, or content producer.
              </p>
              <p>
                All subscriptions are <strong className="text-white">fixed-term and non-recurring</strong> — there is no automatic renewal. Prices are clearly displayed before purchase with no hidden fees.
              </p>
              <p>
                We offer a <strong className="text-white">7-day money-back guarantee</strong> for first-time subscribers. Refund requests are processed within 5–10 business days.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 mt-5">
              <Link to="/terms" className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-4 py-2 text-xs font-medium text-gray-300 transition-colors cursor-pointer">
                <i className="ri-file-text-line text-sky-400" /> Terms &amp; Conditions
              </Link>
              <Link to="/privacy" className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-4 py-2 text-xs font-medium text-gray-300 transition-colors cursor-pointer">
                <i className="ri-shield-check-line text-emerald-400" /> Privacy Policy
              </Link>
              <Link to="/refund" className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-4 py-2 text-xs font-medium text-gray-300 transition-colors cursor-pointer">
                <i className="ri-refund-2-line text-amber-400" /> Refund Policy
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section>
          <div className="bg-gradient-to-br from-sky-950/40 to-gray-900 border border-sky-500/15 rounded-3xl p-8 sm:p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-black text-white mb-3">Ready to Start Watching?</h2>
            <p className="text-gray-400 mb-6 max-w-lg mx-auto">Try our service free for 24 hours — no credit card, no commitment. Join 10,000+ satisfied customers.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/#test"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#0284c7] to-[#0ea5e9] hover:from-[#0369a1] hover:to-[#0284c7] text-white font-bold px-8 py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-gift-line" /> Get Free 24h Trial
              </Link>
              <a
                href="https://wa.me/12089746525"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/30 text-white font-semibold px-8 py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-[#25D366]" /> Contact via WhatsApp
              </a>
            </div>
          </div>
        </section>

      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-8 px-4 text-center text-white/40 text-sm">
        <p>Onetvservices 2019-2026 © All Rights Reserved</p>
        <div className="flex items-center justify-center gap-4 mt-3 flex-wrap">
          <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">Home</Link>
          <span>·</span>
          <Link to="/about" className="text-sky-400 cursor-pointer">About Us</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-white/70 transition-colors cursor-pointer">Terms &amp; Conditions</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-white/70 transition-colors cursor-pointer">Privacy Policy</Link>
          <span>·</span>
          <Link to="/refund" className="hover:text-white/70 transition-colors cursor-pointer">Refund Policy</Link>
        </div>
      </div>
    </div>
  );
}
